import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import * as apiClient from "../api-client";
import { useAppContext } from "../contexts/AppContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaEnvelope, FaLock } from 'react-icons/fa';
import { useEffect, useState } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Helmet } from "react-helmet";
import ReactDOM from 'react-dom';

export type SignInFormData = {
  email: string;
  password: string;
};

const SignInPopup = () => {
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { showToast, setUser } = useAppContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<SignInFormData>();

  const mutation = useMutation(apiClient.signIn, {
    onSuccess: async (data) => {
      setUser(data);
      await queryClient.invalidateQueries("validateToken");
      showToast({ message: "Sign in Successful!", type: "SUCCESS" });

      // Retrieve the stored state from localStorage
      const bookingState = localStorage.getItem("bookingState");
      if (bookingState) {
        const state = JSON.parse(bookingState);
        localStorage.removeItem("bookingState"); // Clear the stored state

        // Navigate back to the booking process with the retrieved state
        window.opener.postMessage({ type: "SIGN_IN_SUCCESS", state }, "*");
        window.close();
      } else {
        const redirectTo = location.state?.from || "/";
        navigate(redirectTo);
      }
    },
    onError: (error: Error) => {
      showToast({ message: error.message, type: "ERROR" });
    },
  });

  const onSubmit = handleSubmit((data) => {
    mutation.mutate(data);
  });

  const closeModal = () => {
    setIsOpen(false);
    navigate(location.state?.from || "/");
  };

  const Modal: React.FC<{ isOpen: boolean; onClose: () => void; children: React.ReactNode }> = ({ isOpen, children }) => {
    if (!isOpen) return null;

    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white p-6 md:p-10 rounded-lg shadow-md w-full max-w-sm md:max-w-md border border-light relative">
          {/* <button
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
            onClick={onClose}
          >
            &times;
          </button> */}
          {children}
        </div>
      </div>,
      document.body
    );
  };

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Sign In to Halo Holidays | Access Your Travel Account</title>
          <meta name="description" content="Already a member? Sign in to your Halo Holidays account to manage your bookings, view your travel history, and access exclusive deals." />
        </Helmet>
      </HelmetProvider>
      <Modal isOpen={isOpen} onClose={closeModal}>
        <form
          className="bg-white p-6 md:p-10 rounded-lg shadow-md w-full max-w-sm md:max-w-md border border-light"
          onSubmit={onSubmit}
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">Sign In</h2>
          <div className="mb-4 relative">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Email
              <div className="flex items-center border rounded w-full py-2 px-3 mt-1 text-gray-700 leading-tight focus:outline-none focus:shadow-outline">
                <FaEnvelope className="mr-3 text-halo-pink" />
                <input
                  type="email"
                  className="flex-1 focus:outline-none"
                  {...register("email", { required: "This field is required" })}
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-xs italic">{errors.email.message}</p>
              )}
            </label>
          </div>
          <div className="mb-6 relative">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Password
              <div className="flex items-center border rounded w-full py-2 px-3 mt-1 text-gray-700 leading-tight focus:outline-none focus:shadow-outline">
                <FaLock className="mr-3 text-halo-pink" />
                <input
                  type="password"
                  className="flex-1 focus:outline-none"
                  {...register("password", {
                    required: "This field is required",
                    minLength: {
                      value: 6,
                      message: "Password must be at least 6 characters",
                    },
                  })}
                />
              </div>
              {errors.password && (
                <p className="text-red-500 text-xs italic">{errors.password.message}</p>
              )}
            </label>
          </div>        
          <div className="flex items-center justify-center mb-6">
            <button
              type="submit"
              className="bg-halo-pink text-white py-2 px-6 rounded hover:bg-pink-800 focus:outline-none focus:shadow-outline"
              aria-label="Sign In"
            >
              Login
            </button>
          </div>
          <div className="flex items-center justify-center">
            <span className="text-sm">
              <Link className="underline text-pink-500" to="/forgot-password">
                Forgot Password?
              </Link>
            </span>
          </div>
          <div className="flex items-center justify-center mt-4">
            <span className="text-sm">
              Don't have an account?{" "}
              <Link className="underline text-pink-500" to="/register-pop-up">
                Register
              </Link>
            </span>
          </div>        
        </form>
      </Modal>
    </>
  );
};

export default SignInPopup;
